import React, { useState, useEffect } from "react";
import { Button } from "../../../components/catalyst/button";
import { Badge } from "../../../components/catalyst/badge";
import {
  UserIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { PencilSquareIcon, CheckCircleIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useNavigate, useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../config/firebaseConfig";
import { FloatingInput } from "../../../components/FloatingInput";
import AlertCard from "../../../components/AlertCardComponent";
import { getStatusBadgeClass } from "../../../util/statusBadge";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import MemberDetailsCard from "../../../components/MemberDetailsCard";
import { useToast } from "../../../util/ToastProvider";
import { collection, getDocs } from "firebase/firestore";
import { useAddReviewLock } from "../../../hooks/addReviewLock";

type Version = {
  id: string;
  time: string;
  user?: string;
  text: string;
  expanded: boolean;
  versionNumber: number;
  isGenerated?: boolean;
  isLatest?: boolean;
  type: "generated" | "manual" | "llm";
  prompt?: string;
};

export default function UpdateReport() {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [reportTitle, setReportTitle] = useState("Loading...");
  const [annexures, setAnnexures] = useState<any[]>([]);
  const [reportStatus, setReportStatus] = useState("loading");
  const [userData, setUserData] = useState();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showAnnexureDeleteDialog, setShowAnnexureDeleteDialog] = useState(false);
  const [selectedAnnexure, setSelectedAnnexure] = useState<any | null>(null);
  const toaster = useToast();

  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  useAddReviewLock(id!);

  // Extract readable text from Firestore objects
  function extractText(obj: any): string {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    if (obj.text) return obj.text;
    if (obj.closingText) return obj.closingText;
    if (obj.openingText) return obj.openingText;
    return JSON.stringify(obj);
  }

  // Fetch report and versions
  async function fetchReportWithVersions() {
    if (!id) return;
    try {
      setLoading(true);
      const performReportReview = httpsCallable(functions, "performReportReview");
      const response = await performReportReview({
        action: "FetchReportWithVersions",
        reportId: id,
      });

      const data = response.data as any;
      const formatted: Version[] = [];

      setReportTitle(data.templateTitle || "Untitled Report");
      setReportStatus(data.status || "unknown");
      setUserData(data.userData);

      // 🟢 Generated data (always version 1)
      if (data.generatedData) {
        const genAt = data.generatedData.generatedAt;
        let generatedTime = "";
        if (genAt?._seconds || genAt?.seconds) {
          const ts = genAt._seconds || genAt.seconds;
          generatedTime = new Date(ts * 1000).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        }

        formatted.push({
          id: "Generated",
          versionNumber: 1,
          time: generatedTime,
          user: "System",
          text: extractText(data.generatedData),
          expanded: false,
          isGenerated: true,
          type: "generated",
        });
      }

      // 🔵 Add reviewedData (map or array)
      if (data.reviewedData) {
        let reviewEntries: any[] = [];
        if (Array.isArray(data.reviewedData)) {
          reviewEntries = data.reviewedData;
        } else if (typeof data.reviewedData === "object") {
          reviewEntries = Object.values(data.reviewedData);
        }

        reviewEntries.sort((a, b) => (a.version || 0) - (b.version || 0));

        reviewEntries.forEach((v: any, i: number) => {
          const text = extractText(v.data);
          const createdAt =
            v.createdAt?.seconds || v.createdAt?._seconds
              ? new Date(
                (v.createdAt?.seconds || v.createdAt?._seconds) * 1000
              ).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
              : "";

          formatted.push({
            id: `review-${v.version || i + 1}`,
            versionNumber: v.version || formatted.length + 1,
            time: createdAt,
            user: v.reviewedBy?.name || "Reviewer",
            text,
            expanded: false,
            type: v.source,
            prompt: v.inputPrompt || null,
          });
        });
      }

      // 🧩 Expand only latest version
      if (formatted.length > 0) {
        formatted[formatted.length - 1].expanded = true;
        formatted[formatted.length - 1].isLatest = true;
      }

      setVersions(formatted);
    } catch (error: any) {
      console.error("❌ Failed to fetch report:", error.message || error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReportWithVersions();
    fetchAnnexures(id || '');
  }, [id]);

  // 🟠 Discard latest version
  async function handleDiscardVersion() {
    if (!id) return;
    try {
      setIsBusy(true);
      const discardFn = httpsCallable(functions, "performReportReview");
      await discardFn({ action: "DiscardLatestVersion", reportId: id });
      await fetchReportWithVersions();
    } catch (err: any) {
      console.error("❌ Discard failed:", err.message || err);
    } finally {
      setIsBusy(false);
      setShowDiscardDialog(false);
    }
  }

  // 🧠 Trigger new review cycle via user input
  async function handleUserInput(userInput: string) {
    if (!id || !userInput.trim()) return;
    try {
      setIsBusy(true);
      const performReportReview = httpsCallable(functions, "performReportReview");
      await performReportReview({
        action: "GenerateReviewCycle",
        reportId: id,
        userInput,
      });
      await fetchReportWithVersions();
    } catch (err: any) {
      console.error("❌ LLM generation failed:", err.message || err);
    } finally {
      setIsBusy(false);
    }
  }

  function handleEditVersion() {
    const latestText = versions[versions.length - 1]?.text || "";
    nav(`/admin/report/edit/${id}`, {
      state: { 
        text: latestText, 
        reportId: id,
        type: "report"
      }
    });
    
  }

  function toggleExpand(versionId: string) {
    if (isBusy) return;
    const latestId = versions[versions.length - 1]?.id;
    if (versionId === latestId) return;
    setVersions((prev) =>
      prev.map((v) =>
        v.id === versionId ? { ...v, expanded: !v.expanded } : v
      )
    );
  }

  async function finishReview() {
    console.log("------------------------")
    if (!id) return;
    try {
      setIsBusy(true);
      const performReportReviewTask = httpsCallable(functions, "performReportReview");
      const res: any = await performReportReviewTask({
        action: "toggleDraftToReview",
        reportId: id,
      });

      toaster.success(res.data?.message || "Status toggled successfully");
      const status = res.data?.status;
      nav(`/admin/report/${id}`);
    } catch (err: any) {
      console.error("Toggle error:", err);
      toaster.error(err.message || "Failed to toggle review mode.");
    } finally {
      setIsBusy(false);
    }
  }

  async function fetchAnnexures(reportId: string) {
    try {
      const annexureRef = collection(db, "reports", reportId, "annexureResults");
      const snapshot = await getDocs(annexureRef);

      const annexureList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by createdAt if needed
      annexureList.sort((a: any, b: any) => {
        const t1 = a.createdAt?.seconds || a.createdAt?._seconds || 0;
        const t2 = b.createdAt?.seconds || b.createdAt?._seconds || 0;
        return t1 - t2;
      });

      setAnnexures(annexureList);
      console.log("✅ Annexures loaded:", annexureList);
    } catch (err) {
      console.error("❌ Error fetching annexures:", err);
    }
  }

  async function handleDeleteAnnexure() {
    if (!id || !selectedAnnexure) return;
  
    try {
      setIsBusy(true);
  
      const performReportReview = httpsCallable(functions, "performReportReview");
      await performReportReview({
        action: "DeleteAnnexure",
        reportId: id,
        annexureId: selectedAnnexure.id,
      });
  
      // Refresh annexure list
      await fetchAnnexures(id);
  
      toaster.success("Annexure deleted successfully.");
  
    } catch (err: any) {
      console.error("❌ Delete annexure failed:", err);
      toaster.error(err.message || "Failed to delete annexure.");
    } finally {
      setIsBusy(false);
      setShowAnnexureDeleteDialog(false);
      setSelectedAnnexure(null);
    }
  }


  
  


  const latestVersion = versions[versions.length - 1];
  const hasMultipleVersions = versions.length > 1;

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center py-20 text-gray-500">
        Loading report...
      </div>
    );
  }

  return (
    <div className="mx-8 my-6 relative">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-[20px] font-semibold text-[rgba(49,44,133,1)]">
          Modify report
        </h2>
        <div className="flex gap-3">
          {/* <Button
            color="soft"
            onClick={() => nav(`/admin/report/${id}`)}
            disabled={isBusy}
          >
            Cancel
          </Button> */}
          <Button color="brand" disabled={isBusy} onClick={() => finishReview()}>
            <CheckCircleIcon className="h-5 w-5 text-white" />
            Finish review
          </Button>
        </div>
      </div>

      <div className="border-t border-[rgba(229,231,235,1)] mb-6"></div>

      <div className="flex gap-2 items-center mb-6">
        <h3 className="text-[24px] font-semibold text-[rgba(49,44,133,1)]">
          {reportTitle}
        </h3>
        <Badge
          className={`${getStatusBadgeClass(
            reportStatus.toLowerCase()
          )} capitalize bold text-xs px-2 py-0.5`}
        >
          {reportStatus}
        </Badge>
      </div>

      {/* Versions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <AlertCard text="You have started reviewing the report. Other administrators will only be able to see it once you finish your review." />
          <AlertCard text="This report contains multiple sections. Please scroll down to see all of them." />

          {versions.map((v, idx) => {
            const isLatest = v.isLatest;
            const showReviewerChip = !v.isGenerated;

            return (
              <div key={v.id}>
                {v.type === "llm" && (
                  <div className="p-4 my-2 rounded-lg bg-gray-100 border border-gray-200">
                    <Badge className="!bg-[rgba(238,242,255,1)] !text-[rgba(55,42,172,1)] text-xs font-medium px-2 py-1 flex items-center gap-1">
                      <UserIcon className="w-4 h-4" /> {v.user}
                    </Badge>
                    <div className="mt-2 text-[14px] font-normal !text-[rgba(74,85,101,1)]">
                      {v.prompt}
                    </div>
                  </div>
                )}

                <div
                  className={clsx(
                    "border border-[rgba(229,231,235,1)] rounded-lg transition-all",
                    v.isGenerated
                      ? "bg-gray-50"
                      : isLatest
                        ? "bg-white shadow-sm"
                        : "bg-[rgba(249,250,251,1)]"
                  )}
                >
                  <div
                    className={clsx(
                      "flex justify-between items-center px-4 py-3",
                      !isLatest && "cursor-pointer"
                    )}
                    onClick={() => toggleExpand(v.id)}
                  >
                    <div className="flex items-center gap-3">
                      {!isLatest && (
                        <>
                          {v.expanded ? (
                            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronRightIcon className="w-5 h-5 text-gray-500" />
                          )}
                        </>
                      )}
                      <span className="text-[14px] text-[rgba(74,85,101,1)] font-medium">
                        Version {v.versionNumber}
                        {v.time && <> • {v.time}</>}
                      </span>
                      {showReviewerChip && v.user && (
                        <Badge className="!bg-[rgba(238,242,255,1)] !text-[rgba(55,42,172,1)] text-xs font-medium px-2 py-1 flex items-center gap-1">
                          <UserIcon className="w-4 h-4" /> {v.user}
                        </Badge>
                      )}
                    </div>

                    {isLatest && (
  <div className="flex gap-2">
    {/* Always show Edit for latest version, even if generated */}
    <Button
      color="soft"
      disabled={isBusy}
      onClick={handleEditVersion}
      className="flex items-center gap-1 text-sm font-medium !text-[rgba(49,44,133,1)]"
    >
      <PencilSquareIcon className="h-4 w-4" />
      Edit text
    </Button>

    {/* Show Discard ONLY if: 
        - Not generated 
        - AND more than one version exists 
    */}
    {!v.isGenerated && hasMultipleVersions && (
      <Button
        color="soft"
        disabled={isBusy}
        onClick={() => setShowDiscardDialog(true)}
        className="flex items-center gap-1 text-sm font-medium text-red-500 border border-red-200 hover:bg-red-50"
      >
        <TrashIcon className="h-4 w-4" />
        Discard
      </Button>
    )}
  </div>
)}

                  </div>

                  {(v.expanded || isLatest) && (
                    <div className="border-t border-[rgba(229,231,235,1)] px-4 py-4">
                      <div className="prose max-w-none text-sm text-gray-800 leading-relaxed">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw, rehypeSanitize]}
                        >
                          {v.text}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {userData && (
          <div className="self-start">
            <MemberDetailsCard data={userData} showMath={true} />
          </div>
        )}
      </div>

      {/* Annexures */}
      {annexures.length > 0 && (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-[2fr,1fr] gap-6">
          <div className="space-y-4">
            {annexures.map((annexure: any) => (
              <div
                key={annexure.id}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm"
              >
                <div className="flex justify-between">
                  <div>
                    { annexure.consentRequired && (<Badge
                      className={`${getStatusBadgeClass(
                        "annexure"
                      )} capitalize bold text-xs px-2 py-0.5 mb-2`}
                    >
                      consent required
                    </Badge>)}
                  </div>
                  <div className="flex gap-4">
                    <Button
                            color="soft"
                            disabled={isBusy}
                            onClick={() => {nav(`/admin/report/edit/${id}`, {
                              state: { 
                                text: annexure.output,
                                reportId: id,
                                annexureId: annexure.id,
                                type: "annexure"
                              }
                            });}}
                            className="flex items-center gap-1 text-sm font-medium !text-[rgba(49,44,133,1)]"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                            Edit text
                          </Button>

                        
                          <Button
                            color="soft"
                            disabled={isBusy}
                            onClick={() => {
                              setSelectedAnnexure(annexure);
                              setShowAnnexureDeleteDialog(true);
                            }}
                            className="flex items-center gap-1 text-sm font-medium text-red-500 border border-red-200 hover:bg-red-50"
                          >
                            <TrashIcon className="h-4 w-4"></TrashIcon>
                             Discard
                          </Button>
                  </div>
                </div>
                
                <h3 className="text-md font-semibold text-gray-800">
                  {annexure.title || `Annexure ${annexure.id}`}
                </h3>

                {annexure.output && (
                  <div className="prose text-sm mt-3 max-w-none text-gray-800 leading-relaxed">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                    >
                      {annexure.output}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}




      <FloatingInput
        onSend={handleUserInput}
        disabled={isBusy}
        loading={isBusy}
        autoCollapseDelay={5000}
      />

      {/* 🧩 Discard Confirmation Dialog */}
      {showDiscardDialog && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-[500px] p-6">
            <h2 className="text-lg font-semibold text-gray-800">
              Discard all changes?
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Are you sure you want to discard all the changes?
            </p>

            <div className="flex justify-end gap-3 mt-5">
              <Button
                color="soft"
                onClick={() => setShowDiscardDialog(false)}
                disabled={isBusy}
              >
                No, go back
              </Button>
              <Button
                color="brand"
                className="text-white"
                onClick={handleDiscardVersion}
                disabled={isBusy}
              >
                Yes, discard
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 🧩 Annexure Delete Confirmation Dialog */}
{showAnnexureDeleteDialog && selectedAnnexure && (
  <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
    <div className="bg-white rounded-xl shadow-xl w-[500px] p-6">
      <h2 className="text-lg font-semibold text-gray-800">
        Delete annexure?
      </h2>

      <p className="text-sm text-gray-600 mt-1">
        Are you certain you want to delete annexure <b>{selectedAnnexure.title || selectedAnnexure.id}</b>? This action cannot be reversed. 
        <br />
      </p>

      <div className="flex justify-end gap-3 mt-5">
        <Button
          color="soft"
          onClick={() => setShowAnnexureDeleteDialog(false)}
          disabled={isBusy}
        >
          Cancel
        </Button>

        <Button
          color="brand"
          className="text-white"
          onClick={handleDeleteAnnexure}
          disabled={isBusy}
        >
          Delete
        </Button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}
